import React, { useState, useEffect } from 'react';
import { FiX, FiDownload, FiPlay, FiPause, FiVolume2, FiVolumeX, FiMaximize, FiMinimize, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { showToast } from '../../components/common/Toaster';
import { learnerDownloadAPI } from '../../api/learnerEndpoints';
import { 
  VideoViewerOverlay,
  VideoViewerContainer,
  VideoViewerHeader,
  VideoViewerBody,
  VideoViewerFooter,
  VideoPlayer,
  VideoControls,
  ControlButton,
  ProgressBar,
  TimeDisplay,
  VolumeControl,
  VideoInfo,
  VideoTitle,
  VideoDescription,
  VideoMeta,
  ActionButtons,
  DownloadButton,
  CloseButton,
} from './VideoViewer.styles';

const VideoViewer = ({ 
  isOpen, 
  onClose, 
  video, 
  onDownload,
  onProgress,
  onVideoEnded,
  initialTime = 0,
  onNext,
  onPrevious,
  hasNext = false,
  hasPrevious = false,
  embedded = false // New prop for embedded mode
}) => {
  const [videoUrl, setVideoUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [progress, setProgress] = useState(0);
  const [hasSetInitialTime, setHasSetInitialTime] = useState(false);
  const [isAutoAdvancing, setIsAutoAdvancing] = useState(false);
  const [isSeeking, setIsSeeking] = useState(false);

  const videoRef = React.useRef(null);
  const progressIntervalRef = React.useRef(null);
  const seekingTimeoutRef = React.useRef(null);

  useEffect(() => {
    if (isOpen && video) {
      loadVideo();
    }
    
    // Clean up seeking state when video changes
    return () => {
      setIsSeeking(false);
      if (seekingTimeoutRef.current) {
        clearTimeout(seekingTimeoutRef.current);
        seekingTimeoutRef.current = null;
      }
    };
  }, [isOpen, video]);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const handleTimeUpdate = () => {
      setCurrentTime(videoElement.currentTime);
      setProgress((videoElement.currentTime / videoElement.duration) * 100);
      
      // Report progress to parent - use the video prop's id, not the video element
      if (onProgress && videoElement.duration && video?.id) {
        onProgress(video.id, videoElement.currentTime, videoElement.duration);
      }
    };

    const handleLoadedMetadata = () => {
      setDuration(videoElement.duration);
      
      // Set initial time (resume from where user left off)
      if (initialTime > 0 && !hasSetInitialTime) {
        videoElement.currentTime = initialTime;
        setHasSetInitialTime(true);
      }
      
      // Autoplay video
      videoElement.play().catch(error => {
        console.log('Autoplay prevented:', error);
        // Autoplay was prevented, user will need to click play
      });
    };

    const handlePlay = () => {
      setIsPlaying(true);
      setIsSeeking(false);
    };
    const handlePause = () => setIsPlaying(false);
    
    const handleSeeking = () => {
      setIsSeeking(true);
      // Clear any existing timeout
      if (seekingTimeoutRef.current) {
        clearTimeout(seekingTimeoutRef.current);
      }
      // Set a timeout to hide seeking indicator if it gets stuck
      seekingTimeoutRef.current = setTimeout(() => {
        setIsSeeking(false);
      }, 5000); // 5 second timeout
    };
    
    const handleSeeked = () => {
      setIsSeeking(false);
      if (seekingTimeoutRef.current) {
        clearTimeout(seekingTimeoutRef.current);
        seekingTimeoutRef.current = null;
      }
    };
    
    const handleWaiting = () => {
      setIsSeeking(true);
      // Clear any existing timeout
      if (seekingTimeoutRef.current) {
        clearTimeout(seekingTimeoutRef.current);
      }
      // Set a timeout to hide seeking indicator if it gets stuck
      seekingTimeoutRef.current = setTimeout(() => {
        setIsSeeking(false);
      }, 5000); // 5 second timeout
    };
    
    const handleCanPlay = () => {
      setIsSeeking(false);
      if (seekingTimeoutRef.current) {
        clearTimeout(seekingTimeoutRef.current);
        seekingTimeoutRef.current = null;
      }
    };
    const handleEnded = () => {
      console.log('Video ended:', video?.id, video?.title);
      setIsPlaying(false);
      // Mark as completed when video ends
      if (onProgress && videoElement.duration && video?.id) {
        console.log('Calling onProgress for video end:', video.id);
        onProgress(video.id, videoElement.duration, videoElement.duration);
      }
      
      // Send pending progress updates to backend
      if (onVideoEnded && video?.id) {
        console.log('Calling onVideoEnded:', video.id);
        onVideoEnded(video.id);
      } else {
        console.log('onVideoEnded not available or no video.id:', { onVideoEnded: !!onVideoEnded, videoId: video?.id });
      }
      
      // Auto-advance to next content when video ends
      if (onNext && hasNext) {
        setIsAutoAdvancing(true);
        setTimeout(() => {
          onNext();
          setIsAutoAdvancing(false);
        }, 2000); // 2 second delay with spinner
      }
    };

    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    videoElement.addEventListener('play', handlePlay);
    videoElement.addEventListener('pause', handlePause);
    videoElement.addEventListener('ended', handleEnded);
    videoElement.addEventListener('seeking', handleSeeking);
    videoElement.addEventListener('seeked', handleSeeked);
    videoElement.addEventListener('waiting', handleWaiting);
    videoElement.addEventListener('canplay', handleCanPlay);

    return () => {
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      videoElement.removeEventListener('play', handlePlay);
      videoElement.removeEventListener('pause', handlePause);
      videoElement.removeEventListener('ended', handleEnded);
      videoElement.removeEventListener('seeking', handleSeeking);
      videoElement.removeEventListener('seeked', handleSeeked);
      videoElement.removeEventListener('waiting', handleWaiting);
      videoElement.removeEventListener('canplay', handleCanPlay);
      
      // Clean up seeking timeout
      if (seekingTimeoutRef.current) {
        clearTimeout(seekingTimeoutRef.current);
        seekingTimeoutRef.current = null;
      }
    };
  }, [videoUrl, initialTime, hasSetInitialTime, onProgress, video, onVideoEnded, onNext, hasNext, isSeeking]);

  const loadVideo = async () => {
    if (!video) return;

    setLoading(true);
    setHasSetInitialTime(false); // Reset for new video
    
    try {
      // Check if it's a direct video URL
      if (video.video_url && video.video_url.startsWith('http')) {
        setVideoUrl(video.video_url);
      } else {
        // Get video URL for viewing (2-hour expiry)
        const response = await learnerDownloadAPI.getVideoUrl(video.id);
        if (response.data.video_url) {
          setVideoUrl(response.data.video_url);
        } else {
          showToast('error', 'Video not available');
        }
      }
    } catch (error) {
      console.error('Video loading error:', error);
      showToast('error', 'Failed to load video');
    } finally {
      setLoading(false);
    }
  };

  const togglePlay = async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      if (isPlaying) {
        video.pause();
      } else {
        await video.play();
      }
    } catch (error) {
      console.error('Video play/pause error:', error);
      // Handle autoplay policy restrictions
      if (error.name === 'NotAllowedError') {
        showToast('error', 'Please click the play button to start the video');
      }
    }
  };

  const handleSeek = (e) => {
    const video = videoRef.current;
    if (!video) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newTime = (clickX / rect.width) * duration;
    video.currentTime = newTime;
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isMuted) {
      video.muted = false;
      video.volume = volume;
      setIsMuted(false);
    } else {
      video.muted = true;
      setIsMuted(true);
    }
  };

  const handleVolumeChange = (e) => {
    const video = videoRef.current;
    if (!video) return;

    const newVolume = parseFloat(e.target.value);
    video.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;

    if (!isFullscreen) {
      if (video.requestFullscreen) {
        video.requestFullscreen();
      } else if (video.webkitRequestFullscreen) {
        video.webkitRequestFullscreen();
      } else if (video.msRequestFullscreen) {
        video.msRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleDownload = async () => {
    if (onDownload) {
      onDownload(video.id);
    } else {
      try {
        const response = await learnerDownloadAPI.getDownloadUrl('video', video.id);
        if (response.data.download_url) {
          const link = document.createElement('a');
          link.href = response.data.download_url;
          link.download = video.title || 'video';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } catch (error) {
        showToast('error', 'Failed to download video');
      }
    }
  };

  if (!isOpen || !video) return null;

  // Render content (shared between embedded and modal modes)
  const renderContent = () => (
    <VideoViewerContainer embedded={embedded} onClick={(e) => e.stopPropagation()}>
      <VideoViewerHeader>
        <VideoInfo>
          <VideoTitle>{video.title}</VideoTitle>
          <VideoDescription>{video.description}</VideoDescription>
          <VideoMeta>
            <span>⏱️ {Math.floor(video.duration / 60)} minutes</span>
            <span>📊 Order: {video.order_index + 1}</span>
          </VideoMeta>
        </VideoInfo>
        <ActionButtons>
          {video.is_downloadable && (
            <DownloadButton onClick={handleDownload}>
              <FiDownload />
            </DownloadButton>
          )}
          {!embedded && (
            <CloseButton onClick={onClose}>
              <FiX />
            </CloseButton>
          )}
        </ActionButtons>
      </VideoViewerHeader>

      <VideoViewerBody embedded={embedded}>
          {loading ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: embedded ? '300px' : '400px',
              background: '#000',
              color: 'white',
              fontSize: '18px'
            }}>
              Loading video...
            </div>
          ) : isAutoAdvancing ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: embedded ? '300px' : '400px',
              background: '#000',
              color: 'white',
              fontSize: '18px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '4px solid rgba(255, 255, 255, 0.3)',
                borderTop: '4px solid #2c5f34',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginBottom: '16px'
              }}></div>
              <div>Loading next content...</div>
            </div>
          ) : videoUrl ? (
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
              <VideoPlayer
                ref={videoRef}
                src={videoUrl}
                embedded={embedded}
                onMouseMove={() => setShowControls(true)}
                onMouseLeave={() => setShowControls(false)}
                onClick={togglePlay}
                onMouseEnter={() => setShowControls(true)}
              />
              {/* Seeking/Buffering Overlay */}
              {isSeeking && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.7)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '16px',
                  zIndex: 10
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid rgba(255, 255, 255, 0.3)',
                    borderTop: '4px solid #2c5f34',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginBottom: '12px'
                  }}></div>
                  <div>Seeking...</div>
                </div>
              )}
            </div>
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: embedded ? '300px' : '400px',
              background: '#000',
              color: 'white',
              fontSize: '18px'
            }}>
              Video not available
            </div>
          )}
        </VideoViewerBody>

        {videoUrl && (
          <VideoViewerFooter 
            show={showControls}
            onMouseEnter={() => setShowControls(true)}
            onMouseLeave={() => setShowControls(false)}
          >
            <VideoControls>
              <ControlButton onClick={togglePlay}>
                {isPlaying ? <FiPause /> : <FiPlay />}
              </ControlButton>

              <TimeDisplay>
                {formatTime(currentTime)} / {formatTime(duration)}
              </TimeDisplay>

              <ProgressBar onClick={handleSeek}>
                <div 
                  style={{
                    width: `${progress}%`,
                    height: '100%',
                    background: '#2c5f34',
                    transition: 'width 0.1s ease'
                  }}
                />
              </ProgressBar>

              <VolumeControl>
                <ControlButton onClick={toggleMute}>
                  {isMuted ? <FiVolumeX /> : <FiVolume2 />}
                </ControlButton>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  style={{
                    width: '80px',
                    margin: '0 8px'
                  }}
                />
              </VolumeControl>

              <ControlButton onClick={toggleFullscreen}>
                {isFullscreen ? <FiMinimize /> : <FiMaximize />}
              </ControlButton>
            </VideoControls>
            
            {/* Navigation Controls - Left and Right */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '20px',
              transform: 'translateY(-50%)',
              zIndex: 10
            }}>
              {hasPrevious && (
                <button 
                  onClick={onPrevious}
                  title="Previous"
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: 'rgba(0, 0, 0, 0.7)',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    fontSize: '20px',
                    backdropFilter: 'blur(10px)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(0, 0, 0, 0.9)';
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.6)';
                    e.target.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(0, 0, 0, 0.7)';
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  <FiChevronLeft />
                </button>
              )}
            </div>
            
            <div style={{
              position: 'absolute',
              top: '50%',
              right: '20px',
              transform: 'translateY(-50%)',
              zIndex: 10
            }}>
              {hasNext && (
                <button 
                  onClick={onNext}
                  title="Next"
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: 'rgba(99, 102, 241, 0.9)',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    fontSize: '20px',
                    backdropFilter: 'blur(10px)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(99, 102, 241, 1)';
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.6)';
                    e.target.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(99, 102, 241, 0.9)';
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  <FiChevronRight />
                </button>
              )}
            </div>
          </VideoViewerFooter>
        )}
    </VideoViewerContainer>
  );

  // Return embedded or modal version
  if (embedded) {
    return renderContent();
  }

  return (
    <VideoViewerOverlay onClick={onClose}>
      {renderContent()}
    </VideoViewerOverlay>
  );
};

export default VideoViewer;
